import { useContext, useEffect, useMemo, useState } from "react";
import "./style/CurrentTheme.css";
import axios from "axios";
import { Link, useLocation, useParams } from "react-router-dom";
import { AuthContext } from "../auth/AuthProvider";
import PhaseLayout from "../phase1_6Layout/PhaseLayout";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import JourneyRoadmap from "../JourneyRoadmap";

let firstAnswer = null;
let secondAnswer = null;
export default function CurrentTheme() {
  const [theme, setTheme] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    user,
    googleUser,
    setUserProgress,
    userProgress,
    themeCompletion,
    setThemeCompletion,
    apiUrl,
  } = useContext(AuthContext);

  const { id } = useParams();

  const storedFirstAnswer = useMemo(() => {
    const stored = localStorage.getItem("first_answer_progress");
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Invalid first_answer_progress JSON:", error);
      return null;
    }
  }, []);

  const storedSecondAnswer = useMemo(() => {
    const stored = localStorage.getItem("second_answer_progress");
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Invalid second_answer_progress JSON:", error);
      return null;
    }
  }, []);

  const firstAnswer =
    storedFirstAnswer?.themeSlug === id ? storedFirstAnswer : null;

  const secondAnswer =
    storedSecondAnswer?.themeSlug === id ? storedSecondAnswer : null;

  const location = useLocation();

  useEffect(() => {
    // localStorage.setItem("location", location.pathname);

    const fetchData = async () => {
      try {
        setLoading(true);

        const [themeRes, questionRes] = await Promise.all([
          axios.get(`${apiUrl}/theme/${id}`),
          axios.get(`${apiUrl}/question/byPhaseAndTheme/Phase 1/${id}`),
        ]);

        setTheme(themeRes.data);
        setQuestions(questionRes.data.sort((a, b) => a.id - b.id));

        const identifier = user
          ? { type: "username", value: user.username }
          : googleUser
            ? {
                type: "googleEmail",
                value: encodeURIComponent(googleUser.email),
              }
            : null;

        if (identifier) {
          const [progressRes, userQuestionRes] = await Promise.all([
            axios.get(
              `${apiUrl}/user-progress/${identifier.type}/${identifier.value}/${id}`,
            ),
            axios.get(
              `${apiUrl}/user-question/by${
                identifier.type === "username" ? "User" : "GoogleUser"
              }AndTheme/${identifier.value}/${id}`,
            ),
          ]);

          const progressData = Array.isArray(progressRes.data)
            ? progressRes.data.sort((a, b) => b.id - a.id)
            : [];

          setUserProgress(progressData);

          const sortedQuestions = Array.isArray(userQuestionRes.data)
            ? userQuestionRes.data.sort((a, b) => b.questionId - a.questionId)
            : [];

          setThemeCompletion(sortedQuestions[0]?.questionThemeIndex || 0);
        } else {
          setUserProgress([]);
          setThemeCompletion(0);
        }
      } catch (error) {
        console.error("Error loading theme data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, id, user, googleUser, setUserProgress, setThemeCompletion]);

  const firstQuestion = questions[0];

  const latestUserProgress =
    Array.isArray(userProgress) && userProgress.length > 0
      ? userProgress[0]
      : null;

  let continuePath = "";

  if (latestUserProgress) {
    continuePath =
      latestUserProgress.answerTitle == null
        ? `/session/${latestUserProgress.themeSlug}/${latestUserProgress.phaseName}/${latestUserProgress.uuid}`
        : `/session/${latestUserProgress.themeSlug}/${latestUserProgress.phaseName}/${latestUserProgress.uuid}/${encodeURIComponent(latestUserProgress.answerTitle)}`;
  } else if (firstAnswer && !secondAnswer) {
    continuePath = `/session/${firstAnswer.themeSlug}/${firstAnswer.phaseTitle}/${firstAnswer.hashed}/${firstAnswer.answerLabel}`;
  } else if (secondAnswer) {
    continuePath = `/session/${secondAnswer.themeSlug}/${secondAnswer.phaseTitle}/${secondAnswer.hashed}/${secondAnswer.answerLabel}`;
  }

  const hasSavedProgress =
    !!latestUserProgress || !!firstAnswer || !!secondAnswer;

  if (loading) return <LoadingSpinner />;

  return (
    <section className="themePlayer">
      <div className="back-btn-div">
        <div>
          <Link
            to={"/session"}
            className="back-btn"
            style={{ color: "rgba(242, 237, 229, 0.78)" }}
          >
            ❮
          </Link>
        </div>
      </div>

      <div className="themePlayerContent">
        <div className="themeBadgeParent">
          <span className="themeBadge">Guided Journey</span>
          <span className="progressBadge">
            Progress:{" "}
            {firstAnswer !== null && secondAnswer !== null
              ? "8%"
              : firstAnswer !== null
                ? "4%"
                : themeCompletion
                  ? Math.floor((themeCompletion / 25) * 100) + "%"
                  : "0%"}
          </span>
        </div>

        <h1 className="themeTitle">{theme.title}</h1>
        <p className="themeDesc">{theme.description}</p>
        <div className="themeOrb" />

        <div className="themeActions">
          {!loading && firstQuestion && (
            <>
              {hasSavedProgress ? (
                <>
                  {continuePath && (
                    <Link to={continuePath} className="btn primary">
                      <span className="btn-icon" aria-hidden="true">
                        <svg width="24" height="24" viewBox="0 0 24 21">
                          <polygon
                            points="8,5 19,12 8,19"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      <span>Continue →</span>
                    </Link>
                  )}

                  <Link
                    to={`/session/${id}/${firstQuestion.phaseTitle}/${firstQuestion.hashed}`}
                    className="btn secondary"
                  >
                    ⟲ Start from beginning
                  </Link>
                </>
              ) : (
                <Link
                  to={`/session/${id}/${firstQuestion.phaseTitle}/${firstQuestion.hashed}`}
                  className="btn primary"
                >
                  Start Phase 1 ➔
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      <div className="journeyParent">
        <h2 className="h2_journey">Journey Roadmap</h2>
        <p className="p_journey">Five phases. Small steps. Saved progress.</p>
        <PhaseLayout />
      </div>
    </section>
  );
}

// import { useContext, useEffect, useState } from "react";
// import "./style/CurrentTheme.css";
// import axios from "axios";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import { AuthContext } from "../auth/AuthProvider";
// import PhaseLayout from "../phase1_6Layout/PhaseLayout";
// import LoadingSpinner from "../loadingSpinner/LoadingSpinner";

// export default function CurrentTheme() {
//   const [theme, setTheme] = useState({});
//   const [questions, setQuestions] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const {
//     user,
//     googleUser,
//     setUserProgress,
//     userProgress,
//     themeCompletion,
//     setThemeCompletion,
//     apiUrl,
//   } = useContext(AuthContext);

//   const { id } = useParams();
//   const navigate = useNavigate();

//   const firstAnswer = localStorage.getItem("first_answer_progress")
//     ? JSON.parse(localStorage.getItem("first_answer_progress"))
//     : null;

//   const secondAnswer = localStorage.getItem("second_answer_progress")
//     ? JSON.parse(localStorage.getItem("second_answer_progress"))
//     : null;

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);

//         // Fetch theme and questions in parallel
//         const [themeRes, questionRes] = await Promise.all([
//           axios.get(`${apiUrl}/theme/${id}`),
//           axios.get(`${apiUrl}/question/byPhaseAndTheme/Phase 1/${id}`),
//         ]);
//         setTheme(themeRes.data);
//         setQuestions(questionRes.data.sort((a, b) => a.id - b.id));

//         // If user or googleUser is logged in, fetch progress
//         const identifier = user
//           ? { type: "username", value: user.username }
//           : googleUser
//             ? { type: "googleEmail", value: googleUser.email }
//             : null;

//         if (identifier) {
//           const [progressRes, userQuestionRes] = await Promise.all([
//             axios.get(
//               `${apiUrl}/user-progress/${identifier.type}/${identifier.value}/${id}`,
//             ),
//             axios.get(
//               `${apiUrl}/user-question/by${identifier.type === "username" ? "User" : "GoogleUser"}AndTheme/${identifier.value}/${id}`,
//             ),
//           ]);

//           setUserProgress(progressRes.data.sort((a, b) => b.id - a.id));
//           const sortedQuestions = userQuestionRes.data.sort(
//             (a, b) => b.questionId - a.questionId,
//           );
//           setThemeCompletion(sortedQuestions[0]?.questionThemeIndex || 0);
//         }
//       } catch (error) {
//         console.error("Error loading theme data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [apiUrl, id, user, googleUser, setUserProgress, setThemeCompletion]);

//   const firstQuestion = questions[0];

//   if (loading) return <LoadingSpinner />;

//   return (
//     <section className="themePlayer">
//       {/* <section className="themePlayerChild"> */}
//       {/* <img src={"/currentTheme.png"} alt="themeImage" id="themeImage" /> */}

//       <div className="back-btn-div">
//         <div>
//           <Link to={"/session"} className="back-btn">
//             ❮
//           </Link>
//         </div>
//       </div>

//       <div className="themePlayerContent">
//         <div className="themeBadgeParent">
//           <span className="themeBadge">Guided Journey</span>
//           <span className="progressBadge">
//             Progress:{" "}
//             {firstAnswer !== null && secondAnswer !== null
//               ? "8%"
//               : firstAnswer !== null
//                 ? "4%"
//                 : themeCompletion
//                   ? Math.floor((themeCompletion / 25) * 100) + "%"
//                   : "0%"}
//           </span>
//         </div>

//         <h1 className="themeTitle">{theme.title}</h1>
//         <p className="themeDesc">{theme.description}</p>
//         <div className="themeOrb" />

//         <div className="themeActions">
//           {!loading && firstQuestion && (
//             <>
//               {firstAnswer !== null ||
//               secondAnswer !== null ||
//               (userProgress && userProgress.length > 0) ? (
//                 <>
//                   <Link
//                     to={
//                       userProgress[0]
//                         ? userProgress[0].answerTitle === null
//                           ? `/session/${userProgress[0].themeSlug}/${userProgress[0].phaseName}/${userProgress[0].uuid}`
//                           : `/session/${userProgress[0].themeSlug}/${userProgress[0].phaseName}/${userProgress[0].uuid}/${encodeURIComponent(userProgress[0].answerTitle)}`
//                         : firstAnswer && !secondAnswer
//                           ? `/session/${firstAnswer.themeSlug}/${firstAnswer.phaseTitle}/${firstAnswer.hashed}/${firstAnswer.answerLabel}`
//                           : `/session/${secondAnswer.themeSlug}/${secondAnswer.phaseTitle}/${secondAnswer.hashed}/${secondAnswer.answerLabel}`
//                     }
//                     className="btn primary"
//                   >
//                     <span className="btn-icon" aria-hidden="true">
//                       <svg width="24" height="24" viewBox="0 0 24 21">
//                         <polygon points="8,5 19,12 8,19" fill="currentColor" />
//                       </svg>
//                     </span>
//                     <span>Continue</span>
//                   </Link>
//                   <Link
//                     to={`/session/${id}/${firstQuestion.phaseTitle}/${firstQuestion.hashed}`}
//                     className="btn secondary"
//                   >
//                     ⟲ Start from beginning
//                   </Link>
//                 </>
//               ) : (
//                 <Link
//                   to={`/session/${id}/${firstQuestion.phaseTitle}/${firstQuestion.hashed}`}
//                   className="btn primary"
//                 >
//                   Start Phase 1 ➔
//                 </Link>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//       {/* </section> */}

//       <PhaseLayout />
//     </section>
//   );
// }
